// R-CONNECT — PersonLink: a small, reusable "person chip" that links a named
// user to their PUBLIC profile (/u/:userId), using the shared <Avatar>. Drop it
// anywhere a person's name appears (roster rows, the هیئت علمی directory,
// community/author bylines, picker results) so every name becomes a doorway to
// the connect surface (follow / connect / DM).
//
// Contract:
//   • With a `userId` → a react-router <a href="/u/:userId"> (uses the app's
//     BrowserRouter; falls through to the page that renders the privacy-gated
//     card + the relationship button cluster). Keyboard-focusable, descriptive
//     aria-label, RTL-safe.
//   • Without a `userId` → degrades to PLAIN, non-interactive text + avatar (no
//     dead link). A list that has no user id yet (e.g. an endpoint that returns
//     only Instructor.id) renders cleanly until the id is added to its select.
//
// It does NOT call any API itself — purely presentational. The profile page is
// the one place that resolves the relationship + privacy.
//
// RTL + tokens + no CDN.
import React from "react";

import { Avatar } from "../ui/Avatar";
import { useGo } from "../router";

export interface PersonLinkProps {
  /** Target User.id. When absent/blank → renders inert plain text (no link). */
  userId?: string | null;
  /** Display name. Falls back to a neutral label when missing. */
  name?: string | null;
  /** Optional served avatar path; the shared Avatar derives a default otherwise. */
  avatarUrl?: string | null;
  /** Avatar diameter (px). Default 32. */
  size?: number;
  /** Role id ("student" …) or colour token for the default-avatar palette. */
  role?: string;
  /** Secondary line under the name (e.g. role label, department). Optional. */
  subtitle?: React.ReactNode;
  /** Extra className on the root. */
  className?: string;
  style?: React.CSSProperties;
}

const NEUTRAL_NAME = "کاربر سامانه";

export const PersonLink: React.FC<PersonLinkProps> = ({
  userId,
  name,
  avatarUrl,
  size = 32,
  role,
  subtitle,
  className,
  style,
}) => {
  const go = useGo();
  const display = (name && String(name).trim()) || NEUTRAL_NAME;
  const id = (userId ?? "").trim();
  const seed = id || display;

  const inner = (
    <>
      <Avatar
        seed={seed}
        name={display}
        avatarUrl={avatarUrl}
        role={role}
        size={size}
      />
      <span style={{ minWidth: 0, flex: "1 1 auto", display: "flex", flexDirection: "column", gap: 1 }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {display}
        </span>
        {subtitle != null ? (
          <span
            style={{
              fontSize: 11.5,
              color: "var(--fg-mute)",
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
    </>
  );

  const rootStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    ...style,
  };

  // No id → inert (no dead link), still shows avatar + name.
  if (!id) {
    return (
      <span className={className} style={rootStyle}>
        {inner}
      </span>
    );
  }

  // BrowserRouter URL for the public profile (route /u/:userId). A real <a href>
  // (so it supports hover-preview of the destination + Cmd/middle-click → new
  // tab + keyboard focus) whose plain-click is intercepted into SPA navigation
  // via useGo — mirrors the sidenav link pattern (no full reload, session kept).
  const onClick = (e: React.MouseEvent): void => {
    // Let the browser handle modifier / non-primary clicks (new tab/window).
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    go("u", id);
  };

  return (
    <a
      href={"/u/" + encodeURIComponent(id)}
      onClick={onClick}
      className={"person-link" + (className ? " " + className : "")}
      aria-label={`نمایش پروفایل ${display}`}
      style={{ ...rootStyle, textDecoration: "none", color: "inherit" }}
    >
      {inner}
    </a>
  );
};

export default PersonLink;
